/**
 * The whole game as one JSON-serialisable object.
 *
 * `run` is everything a Fold resets; the top level is everything that stays
 * (GDD §5.3).
 */

import { PLANETS, STARTING_GC, type PlanetStat } from '../content/planets';
import { FREE_SOCKETS } from '../content/resources';
import { CONTRACT_SLOTS } from '../content/market';

export const SAVE_VERSION = 6;

export type ShipPhase = 'out' | 'back';

export interface PlanetState {
  owned: boolean;
  levels: Record<PlanetStat, number>;
  /** Colony level (0 = uncolonised). Resets on Fold. */
  colony: number;
  /** Ore units mined and waiting for the ship, split by the planet's ore mix on unload. */
  stock: number;
  shipPhase: ShipPhase;
  /** Progress through the current leg, 0..1. */
  shipT: number;
  shipLoad: number;
}

export interface SlotState {
  recipe: string | null;
  /** Seconds of work done on the current unit. */
  progress: number;
  running: boolean;
  /** Accumulated double-output chance; each whole unit is a bonus output. */
  bonus: number;
}

/** A drifting asteroid: tap it before it leaves for a burst of ore (IPM's asteroids). */
export interface Asteroid {
  ore: string;
  amount: number;
  /** Run time it drifts away. */
  until: number;
  /** Position in galaxy units, and drift per second. */
  x: number;
  y: number;
  vx: number;
  vy: number;
}

export interface ActiveChallenge {
  startedAt: number;
  endsAt: number;
  /** Which galaxy is in state.run (on screen). */
  viewing: 'main' | 'challenge';
  /** The galaxy that is not on screen. */
  other: RunState;
}

/** A rover mission scanning one planet, timed on the game clock. Survives the Fold. */
export interface RoverMission {
  planet: number;
  mission: number;
  endsAt: number;
}

export interface FoldOffer {
  te: number;
  distilled: number;
  sc: number;
  drifts: string[];
}

/** A delivery order on the Contracts board: hand over items, get Void Shards. */
export interface Contract {
  id: string;
  item: string;
  qty: number;
  vs: number;
}

export interface RunState {
  /** True for a Challenge galaxy (no Fold, no rover). */
  challenge: boolean;
  time: number;
  gc: number;
  lifetimeGc: number;
  /** IPM's Galaxy Value: GC spent on planets and planet upgrades this galaxy. Pays TE at the Fold. */
  galaxyValue: number;
  planets: PlanetState[];
  hold: Record<string, number>;
  /** Recipes bought this galaxy (IPM charges GC per recipe, per galaxy). */
  recipes: string[];
  smelters: SlotState[];
  crafters: SlotState[];
  /** Completed project ids. */
  projects: string[];
  asteroid: Asteroid | null;
  nextAsteroidAt: number;
  drift: string | null;
}

export interface Lifetime {
  gc: number;
  crafted: number;
  contracts: number;
}

export interface GameState {
  version: number;
  rng: number;
  savedAt: number;
  /** Total game seconds ever simulated (live and offline). Drives the market and contracts. */
  clock: number;
  run: RunState;
  te: number;
  sc: number;
  vs: number;
  /** Item copies fed into each fitting (permanent). */
  fittings: Record<string, number>;
  sockets: number;
  socketed: string[];
  /** Matrix node id → level (0/absent = unallocated; the origin is implicit). */
  matrix: Record<string, number>;
  /** Foundry Room levels; a missing room is unbuilt. */
  rooms: Record<string, number>;
  /** Shipyard hulls owned (permanent). */
  ships: string[];
  /** Deepest planet (1-based) ever owned in a main galaxy; earns hulls. */
  bestDepth: number;
  /** Prospector id → level. */
  prospectors: Record<string, number>;
  /** Planet index (as a string) → assigned prospector id. Kept across Folds. */
  assignments: Record<string, string>;
  /** The rover's current mission, if any. */
  rover: RoverMission | null;
  /** A running Challenge (sim/challenges.ts): the galaxy not on screen waits in `other`. */
  challenge: ActiveChallenge | null;
  /** Clock time the next Challenge may start. */
  challengeReadyAt: number;
  challengesDone: number;
  /** Highest Galaxy Value reached in any Challenge. */
  bestChallenge: number;
  /** The Contracts board; null is an empty slot waiting to refill. */
  contracts: (Contract | null)[];
  /** Clock time each empty slot refills. */
  contractReadyAt: number[];
  folds: number;
  discovered: string[];
  lifetime: Lifetime;
  pendingFold: FoldOffer | null;
  nextId: number;
}

function newPlanet(): PlanetState {
  return {
    owned: false,
    levels: { mine: 1, ship: 1, cargo: 1 },
    colony: 0,
    stock: 0,
    shipPhase: 'out',
    shipT: 0,
    shipLoad: 0,
  };
}

export function newSlot(): SlotState {
  return { recipe: null, progress: 0, running: false, bonus: 0 };
}

/** A fresh galaxy: ◈100 and nothing owned, as IPM starts — buy the first planet yourself. */
export function newRun(drift: string | null, challenge = false): RunState {
  return {
    challenge,
    time: 0,
    gc: STARTING_GC,
    lifetimeGc: 0,
    galaxyValue: 0,
    planets: PLANETS.map(newPlanet),
    hold: {},
    recipes: [],
    smelters: [],
    crafters: [],
    projects: [],
    asteroid: null,
    nextAsteroidAt: 0,
    drift,
  };
}

export function newGame(seed = Date.now()): GameState {
  return {
    version: SAVE_VERSION,
    rng: seed | 0,
    savedAt: Date.now(),
    clock: 0,
    run: newRun(null),
    te: 0,
    sc: 0,
    vs: 0,
    fittings: {},
    sockets: FREE_SOCKETS,
    socketed: [],
    matrix: {},
    rooms: {},
    ships: [],
    bestDepth: 0,
    prospectors: {},
    assignments: {},
    rover: null,
    challenge: null,
    challengeReadyAt: 0,
    challengesDone: 0,
    bestChallenge: 0,
    contracts: Array.from({ length: CONTRACT_SLOTS }, () => null),
    contractReadyAt: Array.from({ length: CONTRACT_SLOTS }, () => 0),
    folds: 0,
    discovered: [],
    lifetime: { gc: 0, crafted: 0, contracts: 0 },
    pendingFold: null,
    nextId: 1,
  };
}
